import React, { Component } from 'react'
import { Stage, Image, Layer, Arrow, Line, Text, Circle, Arc, Label, Tag, RegularPolygon, Group } from 'react-konva'
import Victor from "victor";
import katex from "katex";
import 'katex/dist/katex.min.css';
import LatexText from './LatexText';
import oDotImg from "../../images/png/o_dot.png"
import oTimesImg from "../../images/png/o_times.png"
import useImage from 'use-image';
import { SymbolType } from '../../common/constants';

const clamp = (value, min, max) => {
    return Math.min(Math.max(value, min), max);
}

const degreeToRadian = (deg) => {
    return deg * (Math.PI / 180)
}

const radianToDegree = (rad) => {
    return rad * (180 / Math.PI)
}

const snapValue = (val, snapBy) => {
    return Math.round(val / snapBy) * snapBy;
}

const arraysAreEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }

    return true;
}

class SFVectorClass extends Component {

    labelSize = 22;

    constructor(props) {
        super(props)

        this.degreeTextRef = React.createRef();
        this.xLengthTextRef = React.createRef();
        this.yLengthTextRef = React.createRef();
        this.planeTextRef = React.createRef();

        this.state = {
            points: this.props.points,
            length: this.props.length * this.props.gridValue,
            labelPosition: { x: 0, y: 0 },
            degree: 0,
            degreeTextPosition: { x: 0, y: 0 },
            angleTrianglePos: { x: 0, y: 0 },

            xComponentLength: 0,
            xComponentLablePos: { x: 0, y: 0 },

            yComponentLength: 0,
            yComponentLablePos: { x: 0, y: 0 },
            yComponentOriginLablePos: { x: 0, y: 0 },
            originPoint: { x: 0, y: 0 },
            labelWidth: 0,
        }
    }

    componentDidMount() {
        this.initializeVector();
    }

    componentDidUpdate(prevProps, prevState) {
        let needsRecalculation = false;

        // Check if angle offset changed
        if (prevProps.angleOffset !== this.props.angleOffset) {
            needsRecalculation = true;
        }

        // Check if label position type changed
        if (prevProps.labelPositionType !== this.props.labelPositionType) {
            this.recalculateAllLabelPositions();
        }

        // Check if points changed from props
        if (!arraysAreEqual(prevProps.points, this.props.points)) {
            this.updatePointsFromProps();
            return; // updatePointsFromProps will trigger another update
        }

        // Check if internal points changed
        if (!arraysAreEqual(prevState.points, this.state.points)) {
            needsRecalculation = true;
        }

        // Recalculate everything if needed
        if (needsRecalculation) {
            this.recalculateAll();
        }

        // Update parent component callbacks
        if (prevState.degree !== this.state.degree) {
            this.props.updateVectornAngle(this.props.index, this.state.degree.toFixed(1));
        }

        if (prevState.length !== this.state.length) {
            this.props.updateVectorLength(this.props.index, (this.state.length / this.props.gridValue).toFixed(1));
        }
    }

    initializeVector = () => {
        let points = this.processPoints(this.props.points);
        
        this.setState({ points }, () => {
            this.recalculateAll();
        });
    }

    processPoints = (inputPoints) => {
        let points = [...inputPoints];

        if (this.props.snapToGrid) {
            points = points.map(p => snapValue(p, this.props.gridValue));
        }

        if (this.props.clamp) {
            points = [
                clamp(points[0], 0, this.props.gridWidth),
                clamp(points[1], 0, this.props.gridHeight),
                clamp(points[2], 0, this.props.gridWidth),
                clamp(points[3], 0, this.props.gridHeight)
            ];
        }

        return points;
    }

    updatePointsFromProps = () => {
        let points = this.processPoints(this.props.points);
        
        this.setState({
            points: points,
            originPoint: {
                x: points[0],
                y: points[1],
            },
        });
    }

    recalculateAll = () => {
        const points = this.state.points;
        const basePoints = this.calculateAndReturnBasePoints();
        const degree = this.calculateDegreeValue(...points, ...basePoints);
        const length = this.calculateLengthValue(points);
        
        const xComponentPoints = [points[0], points[1], points[2], points[1]];
        const yComponentPoints = [points[2], points[1], points[2], points[3]];
        const yComponentOriginPoints = [points[0], points[1], points[0], points[3]];

        const xComponentLength = this.calculateComponentLengthValue(xComponentPoints);
        const yComponentLength = this.calculateComponentLengthValue(yComponentPoints);

        const angleTrianglePos = this.calculateAngleTrianglePositionValue(points, degree);
        const degreeTextPosition = this.calculateDegreeTextPositionValue(points, degree, length);

        const labelPosition = this.calculateLabelPositionValue("vector", points, degree);
        const xComponentLablePos = this.calculateLabelPositionValue("x", xComponentPoints, degree);
        const yComponentLablePos = this.calculateLabelPositionValue("y", yComponentPoints, degree);
        const yComponentOriginLablePos = this.calculateLabelPositionValue("y-origin", yComponentOriginPoints, degree);

        this.setState({
            degree,
            length,
            xComponentLength,
            yComponentLength,
            angleTrianglePos,
            degreeTextPosition,
            labelPosition,
            xComponentLablePos,
            yComponentLablePos,
            yComponentOriginLablePos
        }, () => {
            // Update parent with component lengths
            this.props.updateVectorXComponentLength((xComponentLength / this.props.gridValue).toFixed(2));
            this.props.updateVectorYComponentLength((yComponentLength / this.props.gridValue).toFixed(2));
        });
    }

    recalculateAllLabelPositions = () => {
        const points = this.state.points;
        const degree = this.state.degree;
        
        const xComponentPoints = [points[0], points[1], points[2], points[1]];
        const yComponentPoints = [points[2], points[1], points[2], points[3]];
        const yComponentOriginPoints = [points[0], points[1], points[0], points[3]];

        const labelPosition = this.calculateLabelPositionValue("vector", points, degree);
        const xComponentLablePos = this.calculateLabelPositionValue("x", xComponentPoints, degree);
        const yComponentLablePos = this.calculateLabelPositionValue("y", yComponentPoints, degree);
        const yComponentOriginLablePos = this.calculateLabelPositionValue("y-origin", yComponentOriginPoints, degree);

        this.setState({
            labelPosition,
            xComponentLablePos,
            yComponentLablePos,
            yComponentOriginLablePos
        });
    }

    calculateAndReturnBasePoints = () => {
        const points = this.state.points;
        const gridValue = this.props.gridValue;
        const angleOffsetDegrees = this.props.angleOffset * -1;
        const angleOffset = angleOffsetDegrees * Math.PI / 180;

        const x1 = points[0];
        const y1 = points[1];
        const length = gridValue * 2;
        const angle = angleOffset;

        const newX2 = x1 + length * Math.cos(angle);
        const newY2 = y1 + length * Math.sin(angle);

        return [x1, y1, newX2, newY2];
    }

    calculateDegreeValue(x1, y1, x2, y2, x3, y3, x4, y4) {
        let vecA = new Victor(x2 - x1, y2 - y1).normalize();
        let vecB = new Victor(x4 - x3, y4 - y3).normalize();

        let angleRadians = Math.atan2(vecB.y, vecB.x) - Math.atan2(vecA.y, vecA.x);
        let angleDegrees = radianToDegree(angleRadians);

        if (angleDegrees >= 180) {
            angleDegrees -= 360;
        } else if (angleDegrees <= -180) {
            angleDegrees += 360;
        }

        return angleDegrees;
    }

    calculateLengthValue(dataPoints) {
        let vecA = new Victor(dataPoints[0], dataPoints[1]);
        let vecB = new Victor(dataPoints[2], dataPoints[3]);
        return vecA.distance(vecB);
    }

    calculateComponentLengthValue(dataPoints) {
        const [x1, y1, x2, y2] = dataPoints;
        let vecA = new Victor(x1, y1);
        let vecB = new Victor(x2, y2);
        return vecA.distance(vecB);
    }

    calculateAngleTrianglePositionValue(dataPoints, degree) {
        const [x1, y1, x2, y2] = dataPoints;
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        const directionVector = new Victor(midX - x1, midY - y1);

        let angle = degree <= 0 ? -10 : 10;
        if (this.props.angleType == 2) {
            angle = 10;
        }

        const perpendicularVector = directionVector.clone().rotateDeg(angle).normalize();
        const desiredLength = this.props.gridValue;
        const x = x1 + perpendicularVector.x * desiredLength;
        const y = y1 + perpendicularVector.y * desiredLength;

        return { x, y };
    }

    calculateDegreeTextPositionValue(dataPoints, degree, length) {
        const [x1, y1, x2, y2] = dataPoints;
        const degreeInt = parseInt(degree, 10);

        if (degreeInt >= 0 && degreeInt < 35) {
            return { x: x1 + 30, y: y1 + 12 };
        } else if (degreeInt < 0 && degreeInt > -35) {
            return { x: x1 + 30, y: y1 - 12 };
        }

        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        const directionVector = new Victor(midX - x1, midY - y1);

        let angle = degreeInt / 1.7;
        if (length < this.props.minGridValue) {
            angle = degreeInt / 1.5;
        }

        const perpendicularVector = directionVector.clone().rotateDeg(angle).normalize();
        const desiredLength = length < this.props.minGridValue ? this.props.minGridValue : (degreeInt < 0 ? 55 : 50);
        const x = x1 + perpendicularVector.x * desiredLength;
        const y = y1 + perpendicularVector.y * desiredLength;

        return { x, y };
    }

    calculateLabelPositionValue = (type, dataPoints, degree) => {
        const [x1, y1, x2, y2] = dataPoints;
        const centerPoint = new Victor((x1 + x2) / 2, (y1 + y2) / 2);
        const directionVector = new Victor(x2 - x1, y2 - y1);

        let degreeInt = parseInt(degree, 10);

        // Calculate pdeg first based on degree
        let pdeg = -90;
        if (degreeInt < 0) {
            pdeg = 90;
        }

        // Calculate desiredLength
        let desiredLength = -18;
        
        // Apply flip for label position
        if (this.props.isSelectForZeroVector || this.props.labelPositionType === "Automatic Flipped") {
            desiredLength = -desiredLength; // Flip the direction
        }

        // Override for x and y components
        if (type === "x" || type === "y") {
            desiredLength = -10;
            if (Math.abs(degreeInt) > 90) {
                desiredLength = 10;
            }
        }

        const perpendicularVector = directionVector.clone().rotateDeg(pdeg).normalize();
        let endPoint = centerPoint.clone().add(perpendicularVector.clone().multiplyScalar(desiredLength));

        let pointDiff = this.labelSize / 2;
        if (type === "x" || type === "y") {
            pointDiff = 0;
        }

        let pos = { x: endPoint.x - pointDiff, y: endPoint.y - pointDiff };

        // Apply specific adjustments for each type
        if (type === 'x') {
            if (this.xLengthTextRef.current) {
                pos.x -= this.xLengthTextRef.current.textWidth / 2;
            }
            if (degree < 0) {
                pos.y -= this.xLengthTextRef.current?.textHeight || 0;
            }
        } else if (type === 'y') {
            let absDegree = Math.abs(degree);
            if (absDegree > 90 && this.yLengthTextRef.current) {
                pos = {
                    x: pos.x - this.yLengthTextRef.current.textWidth,
                    y: pos.y - this.yLengthTextRef.current.textHeight / 2,
                };
            } else if (absDegree < 90 && this.yLengthTextRef.current) {
                pos = {
                    x: pos.x,
                    y: pos.y - this.yLengthTextRef.current.textHeight / 2,
                };
            }
        } else if (type === 'y-origin') {
            if (this.yLengthTextRef.current) {
                pos.x -= this.yLengthTextRef.current.textWidth / 2;
            }
            if (degree < 0) {
                pos.y -= this.yLengthTextRef.current?.textHeight || 0;
            }
        }

        return pos;
    }

    modifiedPoints = (dataPoints) => {
        const [x1, y1, x2, y2] = dataPoints;
        const dx = x2 - x1;
        const dy = y2 - y1;

        let vectorLength = Math.sqrt(dx * dx + dy * dy);
        if (vectorLength === 0) {
            vectorLength = 1;
        }

        const unitDx = dx / vectorLength;
        const unitDy = dy / vectorLength;

        const newX2 = x1 + (vectorLength - 10) * unitDx;
        const newY2 = y1 + (vectorLength - 10) * unitDy;

        return [x1, y1, newX2, newY2];
    }

    handleMousePointer(event, cursor) {
        const container = event.target.getStage().container();
        container.style.cursor = cursor;
    }

    handleArrowOnDrag = (event, type) => {
        this.props.updateActive(true);

        let target = event.target;
        let x = target.x();
        let y = target.y();

        let previousPoint = type === 'end' ? this.state.points : [...this.props.points];

        let newPoints = [];

        if (this.props.snapToGrid) {
            newPoints = [
                Math.round((previousPoint[0] + x) / this.props.gridValue) * this.props.gridValue,
                Math.round((previousPoint[1] + y) / this.props.gridValue) * this.props.gridValue,
                Math.round((previousPoint[2] + x) / this.props.gridValue) * this.props.gridValue,
                Math.round((previousPoint[3] + y) / this.props.gridValue) * this.props.gridValue,
            ];
        } else {
            newPoints = [
                previousPoint[0] + x,
                previousPoint[1] + y,
                previousPoint[2] + x,
                previousPoint[3] + y,
            ];
        }

        if (this.props.clamp) {
            newPoints = [
                clamp(newPoints[0], 0, this.props.gridWidth),
                clamp(newPoints[1], 0, this.props.gridHeight),
                clamp(newPoints[2], 0, this.props.gridWidth),
                clamp(newPoints[3], 0, this.props.gridHeight),
            ];
        }

        if (type === 'move') {
            this.handleMousePointer(event, 'all-scroll');
            this.setState({ points: newPoints });
        } else {
            this.props.updatePoints(newPoints);
        }

        target.position({ x: 0, y: 0 });
    }

    handleCircleOnDrag = (event, type) => {
        this.props.updateActive(true);

        let target = event.target;
        let x = target.x();
        let y = target.y();

        if (this.props.snapToGrid) {
            x = Math.round(x / this.props.gridValue) * this.props.gridValue;
            y = Math.round(y / this.props.gridValue) * this.props.gridValue;
        }

        if (this.props.clamp) {
            x = clamp(x, 0, this.props.gridWidth);
            y = clamp(y, 0, this.props.gridHeight);
        }

        if (x === this.state.points[0] && y === this.state.points[1]) return;

        let previousPoint = type === 'end' ? [...this.state.points] : [...this.props.points];
        let newPoints = [previousPoint[0], previousPoint[1], x, y];

        if (type === 'move') {
            this.handleMousePointer(event, 'pointer');
            this.setState({ points: newPoints });
        } else {
            this.props.updatePoints(newPoints);
        }

        target.position({ x, y });
    }

    handleOriginOnDrag = (event, type) => {
        let target = event.target;
        let x = target.x();
        let y = target.y();

        if (this.props.snapToGrid) {
            x = Math.round(x / this.props.gridValue) * this.props.gridValue;
            y = Math.round(y / this.props.gridValue) * this.props.gridValue;
        }

        if (this.props.clamp) {
            x = clamp(x, 0, this.props.gridWidth);
            y = clamp(y, 0, this.props.gridHeight);
        }

        if (type === 'move') {
            this.setState({
                originPoint: { x, y },
                points: [x, y, x, y]
            });
        } else {
            this.props.updatePoints([x, this.props.gridHeight - y, x * 5, (this.props.gridHeight - y) * 5]);
        }
        target.position({ x, y });
    }

    calculateArcAngle() {
        let type = this.props.angleType;
        let angle = 0;
        
        switch (type) {
            case 1:
                angle = this.state.degree < 0 ? -this.state.degree : this.state.degree;
                break;
            case 2:
                angle = this.state.degree;
                break;
            case 3:
                angle = this.state.degree < 0 ? -this.state.degree : this.state.degree;
                break;
            default:
                angle = this.state.degree;
                break;
        }
        return angle;
    }

    calculateArcScaleY() {
        let type = this.props.angleType;
        let scale = 1;

        switch (type) {
            case 1:
                scale = this.state.degree > 0 ? -1 : 1;
                break;
            case 2:
                scale = -1;
                break;
            case 3:
                scale = this.state.degree > 0 ? -1 : 1;
                break;
            default:
                scale = 1;
                break;
        }

        return scale;
    }

    calculateArcArrowRotation() {
        let type = this.props.angleType;
        let degree = this.state.degree;
        let rot = 0;

        switch (type) {
            case 1:
                rot = degree < 0 ? (180 - degree) : (360 - degree);
                break;
            case 2:
                rot = (360 - degree);
                break;
            case 3:
                rot = degree < 0 ? (180 - degree) : (360 - degree);
                break;
            default:
                rot = degree;
                break;
        }

        return rot;
    }

    getAngleText() {
        let type = this.props.angleType;
        let angle = 0;
        
        switch (type) {
            case 1:
                angle = this.state.degree < 0 ? -this.state.degree : this.state.degree;
                break;
            case 2:
                angle = this.state.degree;
                break;
            case 3:
                angle = this.state.degree < 0 ? Math.abs(this.state.degree) : -this.state.degree;
                break;
            default:
                angle = this.state.degree;
                break;
        }
        return angle.toFixed(1);
    }

    handleLabelSizeChange = ({ width, height }) => {
        this.setState({ labelWidth: width });
    }

    render() {
        let { points, length, xComponentLength, xComponentLablePos, yComponentLablePos, yComponentLength, yComponentOriginLablePos, degree, angleTrianglePos } = this.state;

        let showVector = true;
         if (this.props.isSelectForZeroVector || this.props.latexSymbol === SymbolType.OUT_ODOT || this.props.latexSymbol === SymbolType.IN_OTIMES || this.props.latexSymbol === SymbolType.SOLID_DOT) {
            showVector = false;
        }

        return (
            <Layer key={this.props.text} listening={!this.props.readOnly} visible={this.props.visible}>
                {/* Debug group */}
                <Group visible={this.props.debug}>
                    <Line points={[points[0], points[1], this.state.degreeTextPosition.x, this.state.degreeTextPosition.y]} stroke={"#bb0000"} />
                    <Line points={[points[0], points[1], angleTrianglePos.x, angleTrianglePos.y]} stroke={"green"} strokeWidth={1} />
                    <Line points={[points[0], points[1], xComponentLablePos.x, xComponentLablePos.y]} stroke={"red"} strokeWidth={1} />
                    <Line points={[points[0], points[1], yComponentLablePos.x, yComponentLablePos.y]} stroke={"blue"} strokeWidth={1} />
                </Group>

                {/* Main Vector Arrow */}
                <Group>
                    <Line
                        visible={showVector}
                        points={this.modifiedPoints(points)}
                        stroke={this.props.strokeColor}
                        strokeWidth={length <= this.props.minGridValue ? 3 : this.props.strokeWidth}
                        fill={this.props.strokeColor}
                    />

                    <RegularPolygon
                        visible={showVector}
                        x={points[2]}
                        y={points[3]}
                        offsetY={-8}
                        sides={3}
                        radius={8}
                        scaleX={length <= this.props.minGridValue ? 1 : 1.1}
                        scaleY={length <= this.props.minGridValue ? 1 : 1.6}
                        rotation={((Math.atan2(points[3] - points[1], points[2] - points[0]) * 180) / Math.PI) + 90}
                        fill={this.props.strokeColor}
                    />

                    {/* X-Axis Components */}
                    <Group visible={(this.props.showComponents || this.props.showComponents2) && Math.abs(parseInt(degree, 10)) !== 90 && Math.abs(parseInt(degree, 10)) !== 180 && !this.props.isSelectForZeroVector}>
                        <Line
                            points={this.modifiedPoints([points[0], points[1], points[2], points[1]])}
                            stroke={this.props.strokeColor}
                            fill={this.props.strokeColor}
                            dashEnabled
                            dash={[7, 4]}
                            strokeWidth={length < this.props.minGridValue ? 3 : 4}
                        />
                        <RegularPolygon
                            x={points[2]}
                            y={points[1]}
                            offsetY={-8}
                            sides={3}
                            radius={xComponentLength < this.props.minGridValue ? 7 : 8}
                            scaleX={xComponentLength < this.props.minGridValue ? 1 : 1.1}
                            scaleY={xComponentLength < this.props.minGridValue ? 1 : 1.6}
                            rotation={Math.abs(degree) > 90 ? -90 : 90}
                            fill={this.props.strokeColor}
                        />
                        <Label visible={this.props.showValues} x={xComponentLablePos.x} y={xComponentLablePos.y} onClick={() => this.props.updateActive(true)}>
                            <Tag fill={this.props.active ? "#f3f383" : "#f0f0f0"} cornerRadius={4} stroke={this.props.active ? "#e4e4e4" : "#ddd"} strokeWidth={1} />
                            <Text ref={this.xLengthTextRef} text={(xComponentLength / 20).toFixed(1)} fill="black" fontSize={21} height={this.labelSize} align="center" fontFamily='sans' />
                        </Label>
                    </Group>

                    {/* Y-Axis Components */}
                    <Group visible={this.props.showComponents && (Math.abs(degree) !== 0 && Math.abs(degree) !== 90 && Math.abs(degree) !== 180) && !this.props.isSelectForZeroVector}>
                        <Line
                            points={this.modifiedPoints([points[2], points[1], points[2], points[3]])}
                            stroke={this.props.strokeColor}
                            fill={this.props.strokeColor}
                            dashEnabled
                            dash={[7, 4]}
                            strokeWidth={length < this.props.minGridValue ? 3 : 4}
                        />
                        <RegularPolygon
                            x={points[2]}
                            y={points[3]}
                            offsetY={-8}
                            sides={3}
                            radius={yComponentLength < this.props.minGridValue ? 7 : 8}
                            scaleX={yComponentLength < this.props.minGridValue ? 1 : 1.1}
                            scaleY={yComponentLength < this.props.minGridValue ? 1 : 1.6}
                            rotation={degree < 0 ? 180 : 0}
                            fill={this.props.strokeColor}
                        />
                        <Label visible={this.props.showValues} x={yComponentLablePos.x} y={yComponentLablePos.y} onClick={() => this.props.updateActive(true)}>
                            <Tag fill={this.props.active ? "#f3f383" : "#f0f0f0"} cornerRadius={4} stroke={this.props.active ? "#e4e4e4" : "#ddd"} strokeWidth={1} />
                            <Text ref={this.yLengthTextRef} text={(yComponentLength / 20).toFixed(1)} fill="black" fontSize={21} height={this.labelSize} align="center" fontFamily='sans' />
                        </Label>
                    </Group>

                    {/* Y-Axis Components From Origin */}
                    <Group visible={this.props.showComponents2 && (Math.abs(degree) !== 0 && Math.abs(degree) !== 90 && Math.abs(degree) !== 180)}>
                        <Line
                            points={this.modifiedPoints([points[0], points[1], points[0], points[3]])}
                            stroke={this.props.strokeColor}
                            fill={this.props.strokeColor}
                            dashEnabled
                            dash={[7, 4]}
                            strokeWidth={length < this.props.minGridValue ? 3 : 4}
                        />
                        <RegularPolygon
                            x={points[0]}
                            y={points[3]}
                            offsetY={-8}
                            sides={3}
                            radius={yComponentLength < this.props.minGridValue ? 7 : 8}
                            scaleX={yComponentLength < this.props.minGridValue ? 1 : 1.1}
                            scaleY={yComponentLength < this.props.minGridValue ? 1 : 1.6}
                            rotation={degree < 0 ? 180 : 0}
                            fill={this.props.strokeColor}
                        />
                        <Label visible={this.props.showValues} x={yComponentOriginLablePos.x} y={yComponentOriginLablePos.y} onClick={() => this.props.updateActive(true)}>
                            <Tag fill={this.props.active ? "#f3f383" : "#f0f0f0"} cornerRadius={4} stroke={this.props.active ? "#e4e4e4" : "#ddd"} strokeWidth={1} />
                            <Text text={(yComponentLength / 20).toFixed(1)} fill="black" fontSize={21} height={this.labelSize} align="center" fontFamily='sans' />
                        </Label>
                    </Group>

                    {/* Degree text */}
                    <Text
                        visible={this.props.showAngle}
                        ref={this.degreeTextRef}
                        fontSize={16}
                        x={this.state.degreeTextPosition.x}
                        y={this.state.degreeTextPosition.y}
                        text={this.getAngleText() + "°"}
                        fill="black"
                        fontStyle='500'
                    />

                    {/* Vector Label */}
                    {(() => {
                        let position = {};
                        if (this.props.labelPositionVec) {
                            position = this.props.labelPositionVec;
                        } else {
                            if (this.props.latexSymbol !== SymbolType.IN_PLANE) {
                                if (this.props.labelPositionType === "Automatic") {
                                    position.x = this.state.points[0] + this.props.latexSymbolSize/2;
                                } else if (this.props.labelPositionType === "Automatic Flipped") {
                                    position.x = this.state.points[0] - this.props.latexSymbolSize;
                                }
                                
                                position.y = this.state.points[1] - this.props.latexSymbolSize /2;
                                
                            } else {
                                position = this.state.labelPosition;
                            }
                        }

                        return (
                            <Label
                                visible={this.props.showLabel}
                                x={position.x}
                                y={position.y}
                                offsetX={this.props.labelPositionType === "Automatic Flipped" ? this.state.labelWidth - 25 : 0}
                                onClick={() => this.props.updateActive(true)}
                            >
                                {/* <Tag fill={this.props.active ? "#f3f383" : "#f0f0f0"} cornerRadius={4} stroke={this.props.active ? "#e4e4e4" : "#ddd"} strokeWidth={1} /> */}
                                <LatexText
                                    onSizeChange={this.handleLabelSizeChange}
                                    text={this.props.text} bgColor={this.props.active ? "#f3f383F2" : "#f0f0f0E6"} borderColor={this.props.active ? "#e9e964" : "#e6e6e6"} />
                            </Label>
                        );
                    })()}
                   {/*  <Label
                        visible={this.props.showLabel}
                        x={this.props.labelPositionVec ? this.props.labelPositionVec.x : this.state.labelPosition.x}
                        y={this.props.labelPositionVec ? this.props.labelPositionVec.y : this.state.labelPosition.y}
                        offsetX={this.props.labelPositionType === "Automatic Flipped" ? this.state.labelWidth - 25 : 0}
                        onClick={() => this.props.updateActive(true)}
                    >
                        <LatexText 
                        onSizeChange={this.handleLabelSizeChange}
                        text={this.props.text} bgColor={this.props.active ? "#f3f383F2" : "#f0f0f0E6"} borderColor={this.props.active ? "#e9e964" : "#e6e6e6"} />
                    </Label> */}

                    {/* Angle Arc */}
                    <Group visible={this.props.showAngle}>
                        <Arc
                            x={points[0]}
                            y={points[1]}
                            innerRadius={this.props.gridValue}
                            outerRadius={this.props.gridValue}
                            fill="transparent"
                            stroke={'#333'}
                            strokeWidth={2}
                            rotation={-this.props.angleOffset}
                            angle={this.calculateArcAngle()}
                            scaleY={this.calculateArcScaleY()}
                        />
                        <RegularPolygon
                            visible={(degree > 15 || degree < -15)}
                            x={angleTrianglePos.x}
                            y={angleTrianglePos.y}
                            sides={3}
                            radius={length <= this.props.gridValue ? 5 : 6}
                            rotation={this.calculateArcArrowRotation() - this.props.angleOffset}
                            fill='black'
                        />
                    </Group>

                    {/* Base Line */}
                    <Line visible={this.props.showAngle} points={this.calculateAndReturnBasePoints()} offsetX={1} stroke={'#000'} strokeWidth={1.5} />

                    {/* Draggable Line */}
                    {this.props.latexSymbol === SymbolType.IN_PLANE ? 
                    <Line
                        points={points}
                        stroke={this.props.debug ? '#ddd' : 'transparent'}
                        strokeWidth={20}
                        draggable={true}
                        onMouseEnter={(event) => this.handleMousePointer(event, 'all-scroll')}
                        onMouseLeave={(event) => this.handleMousePointer(event, 'default')}
                        onDragMove={(event) => this.handleArrowOnDrag(event, 'move')}
                        onDragEnd={(event) => this.handleArrowOnDrag(event, 'end')}
                        onClick={() => this.props.updateActive(true)}
                    />
                    : null}

                    {/* Draggable Circle */}
                    {this.props.disableArrowDrag ? null : 
                        <Circle
                            x={points[2]}
                            y={points[3]}
                            radius={length < this.props.minGridValue ? 10 : 20}
                            stroke={this.props.debug ? '#666' : 'transparent'}
                            fill={this.props.debug ? '#ddd' : 'transparent'}
                            draggable={!this.props.isSelectForZeroVector}
                            onMouseEnter={(event) => this.handleMousePointer(event, 'pointer')}
                            onMouseLeave={(event) => this.handleMousePointer(event, 'default')}
                            onDragMove={(event) => this.handleCircleOnDrag(event, 'move')}
                            onDragEnd={(event) => this.handleCircleOnDrag(event, 'end')}
                            onClick={() => this.props.updateActive(true)}
                        />
                    }

                    {/* Zero Vector Circle */}
                    {this.props.latexSymbol === SymbolType.SOLID_DOT || this.props.isSelectForZeroVector ?
                        <Circle
                            x={points[0]}
                            y={points[1]}
                            radius={this.props.zeroVectorPointRadius}
                            fill={this.props.zeroVectorPointColor}
                            draggable
                            onMouseEnter={(event) => this.handleMousePointer(event, 'all-scroll')}
                            onMouseLeave={(event) => this.handleMousePointer(event, 'default')}
                            onDragMove={(event) => this.handleOriginOnDrag(event, 'move')}
                            onDragEnd={(event) => this.handleOriginOnDrag(event, 'end')}
                            onClick={() => this.props.updateActive(true)}
                        />
                    : null}

                    {/* Latex Symbol */}
                    {this.props.latexSymbol === SymbolType.OUT_ODOT ?
                        <URLImage
                            src={oDotImg}
                            x={points[0]}
                            y={points[1]}
                            offsetX={this.props.latexSymbolSize/2}
                            offsetY={this.props.latexSymbolSize/2}
                            width={this.props.latexSymbolSize}
                            height={this.props.latexSymbolSize}
                            draggable
                            onMouseEnter={(event) => this.handleMousePointer(event, 'all-scroll')}
                            onMouseLeave={(event) => this.handleMousePointer(event, 'default')}
                            onDragMove={(event) => this.handleOriginOnDrag(event, 'move')}
                            onDragEnd={(event) => this.handleOriginOnDrag(event, 'end')}
                            onClick={() => this.props.updateActive(true)}
                        />
                    : null}

                    {this.props.latexSymbol === SymbolType.IN_OTIMES ? 
                        <URLImage 
                            src={oTimesImg} 
                            x={points[0]}
                            y={points[1]} 
                            offsetX={this.props.latexSymbolSize/2}
                            offsetY={this.props.latexSymbolSize/2}
                            width={this.props.latexSymbolSize} 
                            height={this.props.latexSymbolSize}
                            draggable
                            onMouseEnter={(event) => this.handleMousePointer(event, 'all-scroll')}
                            onMouseLeave={(event) => this.handleMousePointer(event, 'default')}
                            onDragMove={(event) => this.handleOriginOnDrag(event, 'move')}
                            onDragEnd={(event) => this.handleOriginOnDrag(event, 'end')}
                            onClick={() => this.props.updateActive(true)}
                        />
                    : null}

                </Group>
            </Layer>
        )
    }
}

const URLImage = ({ src, ...rest }) => {
  const [image] = useImage(src, 'anonymous');
  return <Image image={image} {...rest} />;
};


export default SFVectorClass